<template>
  <div class="sync-element-used-templates">
    <div class="sync-element-used-templates__description text-left mb-3">
      <slot name="description">
        {{ description }}
      </slot>
    </div>
    <slot>
      <div class="progress-spinner__wrapper flex justify-content-center">
        <ProgressSpinner
          style="width: 50px; height: 50px"
          stroke-width="8"
          fill="var(--surface-ground)"
          animation-duration="1.0s"
          aria-label="Custom ProgressSpinner"
          :class="{ block: loadingPreview, hidden: !loadingPreview }"
        />
      </div>
      <Dialog
        v-model:visible="visibleTemplatePreview"
        modal
        :header="t('templateBuilder.templatePreview')"
      >
        <img
          :src="urlTemplateToPreview"
          alt="Loading"
        >
      </Dialog>
      <div
        v-if="!loadingPreview"
        class="sync-element-used-templates__list"
      >
        <SpmTable
          key="sync-element-used-templates"
          ref="syncElementUsedTemplatesDataTableRef"
          v-model="templates"
          :table-columns="columns"
          :paginator="true"
          :show-row-per-page-option="false"
          :custom-selector="unlink"
          :custom-selected-rows="predifinedSelectedRows"
          :expand-rows="useExpandedDataTable"
          grouped-actions-key="id_template"
          index="id_template"
          name=""
        >
          <template
            v-if="useExpandedDataTable"
            #expandedDetails="slotProps"
          >
            <section :key="slotProps.data.id_template">
              <div class="p-1">
                <h4>{{ t('templateBuilder.saveSyncElementModal.correspondingSyncElements') }}</h4>
                <DataTable
                  :value="slotProps.data.syncedElements"
                  class="p-datatable-sm mt-3"
                  responsive-layout="scroll"
                  striped-rows
                >
                  <Column
                    field="name"
                    :header="t('templateBuilder.saveSyncElementModal.templateListView.columns.name')"
                  />
                  <Column
                    field="type"
                    :header="t(`templates.type`)"
                  >
                    <template #body="templateData">
                      {{ getSyncElementLabel(templateData.data) }}
                    </template>
                  </Column>
                </DataTable>
              </div>
            </section>
          </template>
          <template #label="slotProps">
            <i
              class="fa-regular fa-magnifying-glass mr-2"
              style="cursor: pointer;"
              @click="(event) => preview(event, slotProps.data.id_template, slotProps.data.type)"
            />
            <span class="font-bold capitalize">
              {{ slotProps.data[slotProps.column.props.field] }}
            </span>
          </template>
          <template #type="slotProps">
            {{ t(`templateSelector.dialogTypeTemplate.types.${slotProps.data[slotProps.column.props.field]}.label`) }}
          </template>
        </SpmTable>
      </div>
    </slot>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  ref,
  computed,
} from 'vue';

import ProgressSpinner from 'primevue/progressspinner';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SpmTable from '@/components/table/SpmTable.vue';

import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';

import {
  SpmTableState,
  SpmTableColumns,
  SelectedSyncElement,
  TemplateStructureEnum,
  WidgetTypeEnum,
} from '@/types';

import { getSyncElementNameAndTypeBySyncId, getSyncElementsIdsInTemplate, TemplateEditorState as state } from '@/composables/template-editor/TemplateEditor';
import { AutomatedScenarioState } from '@/composables/AutomatedScenarios';

import getTemplatesUsedBySyncElement from '@/composables/template/SyncElement';
import { loadImageUntilSuccess } from '@/composables/loadImage';
import { getWidgetLabelTranslationKey, getWidgetType, isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import CryptoJS from 'crypto-js';
import { TEMPLATE_SYNC_ELEMENT_IDENTIFIER, TEMPLATE_SYNC_ELEMENT_IDs } from './utils/constants';

export default defineComponent({
  name: 'SyncElementUsedTemplates',

  components: {
    ProgressSpinner,
    SpmTable,
    Dialog,
    DataTable,
    Column,
  },

  props: {
    syncElementId: {
      type: String,
      required: true,
    },

    idShop: {
      type: Number,
      required: false,
      default: 0,
    },

    description: {
      type: String,
      required: false,
      default: 'Used templates with sync element',
    },

    unlink: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['loaded'],

  setup(props: { idShop: number; syncElementId: string; unlink: boolean }, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const templates = ref<SpmTableState>({
      items: [],
      error: '',
      total: 0,
    });

    const loadingPreview = ref(false);
    const baseUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    const urlTemplateToPreview = ref('');
    const visibleTemplatePreview = ref(false);
    const syncElementUsedTemplatesDataTableRef = ref();
    const predifinedSelectedRows = ref<any[]>([]);

    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);
    const useExpandedDataTable = ref(false);

    const columns: SpmTableColumns[] = [
      {
        field: 'id_template',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        hidden: true,
        style: '',
        type: 'preview',
      },
      {
        field: 'label',
        header: t('templates.name'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'date_modification',
        header: t('campaigns.bulk.list.columns.last_update'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        filterSettings: { type: 'date', hideFilterMenu: true },
      },
    ];

    const preview = async (event: Event, idTemplate: number, typeTemplate: any) => {
      event.stopPropagation();
      const targetElement = event.target as HTMLElement;
      targetElement.classList.remove('fa-magnifying-glass');
      targetElement.classList.add('fa-spinner', 'fa-spin');

      urlTemplateToPreview.value = '';
      const cryptedImageKey = CryptoJS.SHA1(`salt-${idTemplate}`).toString();

      const potentialImageUrl = `${baseUrl}${props.idShop}_${cryptedImageKey}${isDisplayTemplate(typeTemplate) ? '-isdisplay' : ''}.png&w=400&${new Date().getTime()}`;
      try {
        await loadImageUntilSuccess(potentialImageUrl);
        urlTemplateToPreview.value = potentialImageUrl;
        visibleTemplatePreview.value = true;
      } catch (error) {
        console.error("Erreur lors du chargement de l'image", error);
      }

      targetElement.classList.remove('fa-spinner', 'fa-spin');
      targetElement.classList.add('fa-magnifying-glass');
    };

    const getSelectedRows = () => {
      if (syncElementUsedTemplatesDataTableRef.value) {
        return syncElementUsedTemplatesDataTableRef.value.getSelectedRows();
      }
      return [];
    };

    const getSyncElementLabel = (data: {
      type: string;
      partType?: string;
      subtype?: string;
    }) => {
      if (data.partType) {
        return t(
          `templateBuilder.panels.loadElements.chip.elementPart.${data.partType}`,
          [
            t(
              `templateBuilder.panels.loadElements.chip.elementPart.elements.${data.type}`,
              [data.subtype ? t(getWidgetLabelTranslationKey(getWidgetType(data.subtype) as WidgetTypeEnum)) : ''],
            ),
          ],
        );
      }
      return t(`templateBuilder.panels.loadElements.chip.${data.type}`);
    };

    onMounted(async () => {
      loadingPreview.value = true;
      const syncElementIds = [props.syncElementId];
      const syncElements: any[] = [];
      if (selectedSyncElement.value
        && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION
        && !selectedSyncElement.value.elementPartType
        && !props.unlink
      ) {
        const syncWidgetsIds = getSyncElementsIdsInTemplate(`#${selectedSyncElement.value.elementId}`);
        if (syncWidgetsIds.length) {
          syncElementIds.push(...syncWidgetsIds);
        }
        syncElementIds.forEach((syncElementId) => {
          syncElements.push(getSyncElementNameAndTypeBySyncId(syncElementId));
        });
      }

      templates.value = await getTemplatesUsedBySyncElement({ idShop: props.idShop, syncElementIds });
      const templatesInState = state.templates.filter((template) => {
        const section = template.sections.find((item) => {
          const data = JSON.parse(item.data || '{}');
          if (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] && syncElementIds.includes(data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER])) {
            return true;
          }
          if (data[TEMPLATE_SYNC_ELEMENT_IDs] && data[TEMPLATE_SYNC_ELEMENT_IDs].find((syncId: string) => syncElementIds.includes(syncId))) {
            return true;
          }
          return false;
        });
        if (section) return true;
        return false;
      }).map((template) => ({
        id_template: template.id,
        label: template.informations.name,
        type: template.type,
        template_config: JSON.stringify(template.configuration),
        date_modification: template.dateModification,
      }));
      templatesInState.forEach((template) => {
        if (!templates.value.items.find((item: any) => item.id_template.toString() === template.id_template.toString())) {
          templates.value.items.push(template);
          templates.value.total += 1;
        }
      });

      if (AutomatedScenarioState && AutomatedScenarioState.isEditorVisible && AutomatedScenarioState.scenario && AutomatedScenarioState.scenario.id) {
        const templatesInAutomatedScenario = state.templatesLinkedToCampaign
          .filter((template) => template.idCampaign === AutomatedScenarioState.scenario.id)
          .filter((template) => {
            const section = template.sections.find((item) => {
              const data = JSON.parse(item.data || '{}');
              if (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] && syncElementIds.includes(data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER])) {
                return true;
              }
              if (data[TEMPLATE_SYNC_ELEMENT_IDs] && data[TEMPLATE_SYNC_ELEMENT_IDs].find((syncId: string) => syncElementIds.includes(syncId))) {
                return true;
              }
              return false;
            });
            if (section) return true;
            return false;
          }).map((template) => ({
            id_template: template.id,
            label: `${template.informations.name} (${AutomatedScenarioState.scenario.settingsData.name})`,
            type: template.type,
            template_config: JSON.stringify(template.configuration),
            date_modification: template.dateModification,
          }));
        templatesInAutomatedScenario.forEach((template) => {
          if (!templates.value.items.find((item: any) => item.id_template.toString() === template.id_template.toString())) {
            templates.value.items.push(template);
            templates.value.total += 1;
          }
        });
      }

      // Group templates by sync element if multiple sync elements
      if (syncElements.length > 1 && !props.unlink) {
        useExpandedDataTable.value = true;
        templates.value.items.forEach((template: any) => {
          const templateItemConfig = JSON.parse(template.template_config || '{}');
          if (templateItemConfig[TEMPLATE_SYNC_ELEMENT_IDs] && Array.isArray(templateItemConfig[TEMPLATE_SYNC_ELEMENT_IDs])) {
            templateItemConfig[TEMPLATE_SYNC_ELEMENT_IDs].forEach((syncId: string) => {
              if (syncElements.find((syncElement) => syncElement.syncElementId === syncId)) {
                if (!template.syncedElements) {
                  // eslint-disable-next-line no-param-reassign
                  template.syncedElements = [];
                }
                // eslint-disable-next-line no-param-reassign
                template.syncedElements.push(syncElements.find((syncElement) => syncElement.syncElementId === syncId));
              }
            });
          }
        });
      }

      if (props.unlink) {
        // Sort the template to put the current template first
        templates.value.items.sort((a: any, b: any) => {
          if (state.template) {
            if (a.id_template === state.template.id) return -1;
            if (b.id_template === state.template.id) return 1;
          }
          return 0;
        });
        if (templates.value.items.length) {
          predifinedSelectedRows.value = [(templates.value.items as any[])[0].id_template];
        }
      }
      loadingPreview.value = false;
      emit('loaded', templates.value.total);
    });

    return {
      t,
      templates,
      columns,
      loadingPreview,
      urlTemplateToPreview,
      visibleTemplatePreview,
      syncElementUsedTemplatesDataTableRef,
      predifinedSelectedRows,
      useExpandedDataTable,
      preview,
      getSelectedRows, // for ref
      getSyncElementLabel,
    };
  },
});
</script>
